/* jshint esversion: 6 */
/* eslint-env browser */

import React, { Fragment } from 'react';
import Email from 'react-email-autocomplete';
import PhoneInput from 'react-phone-input-2';
import PropTypes from 'prop-types';
import xss from 'xss';

import ReliableRequest from '../../../lib/ReliableRequest';
import Serialize from '../../../lib/Serialize';
import * as UUID from '../../../lib/uuid';
import { isValidEmail } from '../../../lib/Validate';

import WebsiteWidgetChat from './WebsiteWidgetChat';
import WebsiteWidgetCloseForm from './WebsiteWidgetCloseForm';
import WebsiteWidgetDropdown from './WebsiteWidgetDropdown';

import AvochatoImage from '../../../images/favicon-256.ico';
import MutedIcon from '../../../images/mute.svg';
import UnmutedIcon from '../../../images/volume-2.svg';
import BackIcon from '../../../images/back.svg';

class WebsiteWidgetForm extends React.Component {
  constructor(props) {
    super(props);
    const { headerText } = this.props;
    this.state = {
      title: headerText,
      temporaryId: UUID.generate(),
      messages: new Map(),
      status: 'open',
      name: '',
      nameValid: true,
      phone: '',
      phoneValid: true,
      email: '',
      emailValid: true,
      timeLeft: 30,
      closing: false,
      muted: false,
      marketingConsentChecked: false,
    };
    this.muted = false;
    this.lastPhone = props.countryCode;
    this.widgetForm = React.createRef();
    this.websiteWidgetDropdown = React.createRef();
    this.chatInterface = React.createRef();
    this.notificationAudio = React.createRef();
  }

  render() {
    const {
      className, iframeDomain, initialDomain, isMobile, sessionId, widgetId,
    } = this.props;
    const { temporaryId } = this.state;
    const mobileClass = isMobile ? 'mobile' : '';
    return (
      <form
        id="text_me-form"
        className={`${className} no-select ${mobileClass}`}
        ref={this.widgetForm}
        onSubmit={(e) => this.onSubmit(e)}
      >
        <input type="hidden" name="widget_id" value={widgetId || ''} />
        <input type="hidden" name="live_session" value={sessionId || ''} />
        <input type="hidden" name="iframe_domain" value={iframeDomain || ''} />
        <input type="hidden" name="initial_domain" value={initialDomain} />
        <input type="hidden" name="temporary_id" value={temporaryId || ''} />

        {this.renderHeader()}
        {this.renderBody()}
        {this.renderWhitelabel()}
      </form>
    );
  }

  renderHeader() {
    return(
      <Fragment>
        <div id="title" style={{ background: this.props.primaryColor }}>
          <div className="inner">
            <span id="mobile-back-btn" onClick={() => this.minimizeForm()}><img src={BackIcon} alt="Back" className="back-icon" role="button" tabIndex="2" aria-label="Hide chat" /></span>
            <span id="closer" role="button" aria-label="Close chat form" tabIndex="1" onClick={() => this.minimizeForm()} onKeyDown={(e) => { if (e.key == " " || e.code == "Space" || e.keyCode == 32) { this.minimizeForm() }} }>
              {this.renderMuteButton()}
              <span id="hard-close-button" role="button" tabIndex="2" aria-label="Exit chat" onKeyDown={(e) => { if (e.key == " " || e.code == "Space" || e.keyCode == 32) { this.startCloseForm(e) }} }  onClick={(e) => this.startCloseForm(e)}>×</span>
            </span>
            <span id="text-me-title-fixed" style={{
              color: this.props.textColor,
              ...this.props.shadowEnabled && { 'text-shadow': '-1px 0 rgba(0,0,0,0.2), 0 1px rgba(0,0,0,0.2), 1px 0 rgba(0,0,0,0.2), 0 -1px rgba(0,0,0,0.2)'},
            }}>{this.state.title}</span>
          </div>
        </div>
      </Fragment>
    );
  }

  renderBody() {
    // if user is chatting already let them finish chatting before disabling their chat
    if (this.props.disabled && this.props.status === 'qualification') {
      return (
        <div id="text_me-fields">
          <div
            id="disabled_text"
            dangerouslySetInnerHTML={{
              __html: this.props.disabledHTML.replace(/href/g, "target='_blank' rel='noopener noreferrer' href"),
            }} />
        </div>
      );
    }

    switch (this.props.status) {
      case 'qualification':
        return (
          <>
            {this.renderForm()}
          </>
        );
      case 'chatting':
        return (
          <>
            {this.renderCloseForm()}
            {this.renderChat()}
          </>
        );
      case 'thank_you':
        this.startCountdown();

        return (
          <div id="text_me-fields">
            <div id="thank-you-time-left">Your session will reload in {this.state.timeLeft} second{this.state.timeLeft === 1 ? '' : 's'}</div>
            <div id="thank-you-body">
              Thanks, you should receive a text to "{this.state.phone}" shortly.
            </div>
            {this.renderNumber(this.props.showNumberThankYou)}
          </div>
        );
    }
  }

  renderForm() {
    return (
      <div id="text_me-fields" className={this.state.closing ? 'closing' : 'opening'}>
        <input type="hidden" name="authenticity_token" value={this.props.authenticityToken} />

        <input type="text" name="name" id="name"
          className={(this.state.nameValid) ? 'valid' : 'invalid' }
          value={this.state.name}
          placeholder="What's your name? *"
          autoComplete="off"
          onChange={(e) => {
            const {value} = e.target;

            this.setState({
              name: value,
              nameValid: value.length > 0,
            });
          }} />
        <PhoneInput
          inputClass={(this.state.phoneValid) ? 'valid' : 'invalid'}
          country='us'
          value={this.state.phone}
          tabIndex={-1}
          countryCodeEditable={false}
          placeholder={'+1 (000) 000-0000'}
          onlyCountries={['us', 'ca']}
          inputProps={{
            name: 'phone',
            required: true,
          }}
          phoneValid={this.state.phone.length >= 17}
          onFocus={(phone, value) => this.handleFocus(phone, value)}
          onChange={phone => this.setState({ phone })}
        />

        {this.renderEmail()}
        {this.renderDropdown()}
        {this.renderMessage()}

        {this.renderDisclaimerSection()}
        {this.renderLiveChatButton()}
        {this.renderTextWithUsButton()}
        {this.renderNumber(this.props.showNumber)}
      </div>
    );
  }
  renderDisclaimerSection() {
    const { marketingConsentOptional, textWithUsText, disclaimer, marketingConsentText } = this.props;
    const { marketingConsentChecked } = this.state;
    // New widgets: transactional disclaimer text + optional marketing consent checkbox below
    if (marketingConsentOptional && textWithUsText) {
      return (
        <div id="disclaimer-container" className="split-consent">
          <div id="disclaimer" dangerouslySetInnerHTML={{
            __html: disclaimer,
          }} />
          <label className="marketing-consent-label">
            <input
              name="marketing_consent"
              type="checkbox"
              checked={marketingConsentChecked}
              onChange={this.handleMarketingConsentCheck}
            />
            <span className="marketing-consent-text">{marketingConsentText}</span>
          </label>
        </div>
      );
    }
    // Existing widgets: original layout - checkbox inline with disclaimer (checkbox gates form)
    // Note: this checkbox is NOT for marketing consent, it's the original disclaimer agreement
    return (
      <div id="disclaimer-container" className="legacy-consent">
        {textWithUsText && (
          <input
            name="disclaimerCheck"
            type="checkbox"
            checked={marketingConsentChecked}
            onChange={this.handleMarketingConsentCheck}
          />
        )}
        <div id="disclaimer" dangerouslySetInnerHTML={{
          __html: disclaimer,
        }} />
      </div>
    );
  }

  renderNumber(shouldRender) {
    if (!shouldRender) return null;

    return (
      <Fragment>
        <div className="or-bar">
          <span>or</span>
        </div>
        <div className="text-us">
          Text us @ <a href={'sms:' + this.props.number}>{this.props.number}</a>
        </div>
      </Fragment>
    );
  }


  renderWhitelabel() {
    if (this.props.whiteLabel || this.props.status === "chatting") return null;

    return (
      <div id="powered-by" className="no-select">
        <img
          id="logo-img"
          alt="Powered by Avochato"
          src={AvochatoImage} />
        Powered by Avochato
      </div>
    );
  }

  renderCloseForm() {
    const { headerText } = this.props;

    return (
      <WebsiteWidgetCloseForm
        className={this.state.status === 'closing' ? null : 'hide'}
        onCancel={() => this.setState({title: headerText, status: 'open'})}
        onConfirm={() => this.closeForm(true)}
        primaryColor={this.props.primaryColor}
        textColor={this.props.textColor}
        shadowEnabled={this.props.shadowEnabled}
      />
    );
  }

  renderChat() {
    return (
      <Fragment>
        <audio
          ref={this.notificationAudio}
          className="hide"
          src="https://assets.avochato.com/samples/alert_woodblock.mp3"
          type="audio/mp3" />
        <WebsiteWidgetChat
          ref={this.chatInterface}
          className={this.state.status === 'open' ? null : 'hide'}
          chatMessageInputRef={(e) => this.chatMessageTextarea = e}
          sessionId={this.props.sessionId}
          messages={Array.from(this.state.messages.values())}
          onEvent={(e) => this.handleMessageEvents(e)}
          onMissing={() => this.closeForm(false)}
          headerChange={(e) => this.handleHeaderChange(e)}
          primaryColor={this.props.primaryColor}
          whiteLabel={this.props.whiteLabel}
        />
      </Fragment>
    );
  }

  renderEmail() {
    if (!this.props.emailEnabled) return null;

    return (
      <Email
        className={(this.state.emailValid) ? 'valid' : 'invalid'}
        name="email"
        placeholder="Your Email *"
        domains={[
          'gmail.com',
          'icloud.com',
          'yahoo.com',
          'hotmail.com',
          'me.com',
          'aol.com',
          'mac.com',
          'live.com',
          'googlemail.com',
          'msn.com',
          'facebook.com',
          'verizon.net',
          'outlook.com',
          'rediffmail.com',
          'zohomail.in',
          'zoho.com',
          'mail.com',
          'protonmail.com',
          'yandex.com'
        ]}
        onChange={(e) => this.validateEmail(e)}
        onBlur={(e) => this.validateEmail(e)}
        required />
    );
  }

  validateEmail(event) {
    this.setState({
      email: event.target.value,
      emailValid: isValidEmail(event.target.value),
    });
  }

  renderDropdown() {
    if (!this.props.dropDownEnabled || this.props.dropDownOptions.length === 0) return null;

    return <WebsiteWidgetDropdown ref={this.websiteWidgetDropdown} prompt={this.props.dropDownPrompt} options={this.props.dropDownOptions} />;
  }

  renderMessage() {
    if (!this.props.messageEnabled) return null;

    return (
      <textarea name="help_message" placeholder="How can we help you?" id="help-message" ref={(e) => this.helpMessageTextarea = e} />
    );
  }

  disableEmailButton() {
    const {name, email, emailValid} = this.state;
    if (!this.props.emailEnabled) return false

    return name.length <= 0 || email.length <= 0 || !emailValid;
  }

  renderLiveChatButton() {
    if (!this.props.liveChatText) return null;

    return (
      <input type="submit" name="commit"
        className="submit"
        title={(this.disableEmailButton()) ? 'Please enter a name and email' : null}
        value={this.props.liveChatText}
        disabled={this.disableEmailButton()}
        style={{
          background: this.props.submitButtonColor,
          color: this.props.submitTextColor,
          ...this.props.shadowEnabled && { 'text-shadow': '-1px 0 rgba(0,0,0,0.2), 0 1px rgba(0,0,0,0.2), 1px 0 rgba(0,0,0,0.2), 0 -1px rgba(0,0,0,0.2)'},
        }}/>
    );
  }

  disableTextWithUsButton() {
    const {marketingConsentChecked, name, phone} = this.state;
    const { marketingConsentOptional } = this.props;
    const strippedPhone = phone.replace(/[^\d]/g, '');
    // For new widgets (marketing_consent_optional=true): checkbox is optional
    // For existing widgets (marketing_consent_optional=false): checkbox gates form
    const consentRequired = !marketingConsentOptional && !marketingConsentChecked;
    return consentRequired || name.length <= 0 || strippedPhone.length < 11;
  }

  renderTextWithUsButton() {
    if (!this.props.textWithUsText) return null;

    return (
      <input type="submit" name="commit" className="submit"
        value={this.props.textWithUsText}
        title={(this.disableTextWithUsButton()) ? 'Please enter a name and phone number' : 'Text with us'}
        onClick={(e) => this.textWithUs(e)}
        disabled={this.disableTextWithUsButton()}
        style={{
          background: this.props.submitButtonColor,
          color: this.props.submitTextColor,
          ...this.props.shadowEnabled && { 'text-shadow': '-1px 0 rgba(0,0,0,0.2), 0 1px rgba(0,0,0,0.2), 1px 0 rgba(0,0,0,0.2), 0 -1px rgba(0,0,0,0.2)'},
        }}
      />
    );
  }

  /* Event handlers */
  startCountdown() {
    if (this.countdown) return;

    this.countdown = setTimeout(() => {
      if (this.state.timeLeft === 0) return this.closeForm(false);

      this.setState({timeLeft: this.state.timeLeft - 1});
      this.countdown = null;
      this.startCountdown();
    }, 1000);
  }

  handleFocus(phone, value) {
    if (!!value.name) return;

    return this.setState({
      phone: '+1',
    });
  }

  // eslint-disable-next-line
  onPhoneChange(phone) {
    const strippedPhone = phone.replace(/[^+\d]/g, '');

    const { countryCode } = this.props;

    // regexp should be format \+{d}?... to trim out all leading country digits
    const regexpCountryCode = new RegExp(`\\${countryCode.split('').join('?')}?`);
    const newPhone = strippedPhone.replace(regexpCountryCode, countryCode);

    // use same value on a double event (triggered by autofill)
    if (this.lastPhone !== newPhone) {
      this.lastPhone = newPhone;
    }
    return this.setState({
      phone: newPhone,
      phoneValid: newPhone.length >= 12,
    });
  }

  handleMessageEvents(e) {
    switch (e.event) {
      case 'SmsMessage':
      case 'MmsMessage':
        if (e.direction === 'out' && !this.state.messages.has(e.temporary_id)) {
          const now = new Date();
          // only send notification if it's a recent message
          if (now.getTime() - e.sent_at < 5000) this.sendNotification();
        }
        this.state.messages.set(e.temporary_id, {
          contents: e.contents,
          type: e.event,
          id: e.temporary_id,
          direction: e.direction,
          messageText: xss(e.message),
          status: 'delivered',
          sentAt: e.sent_at,
        });
        this.setState({messages: this.state.messages});
        break;
      case 'MuteChange':
        this.muted = e.mutedState;
        break;
    }
  }

  sendNotification() {
    if (this.state.muted) return;

    const {current} = this.notificationAudio;
    if (current) {
      // hack to get browser to not error when sending audio
      current.click();
      current.play();
    }
  }

  minimizeForm() {
    if (typeof(this.props.onMinimize) === 'function') this.props.onMinimize();
  }

  onToggleMute(e) {
    e.stopPropagation()
    const mutedState = !this.state.muted;
    this.setState({muted: mutedState});
  }

  renderMuteButton() {
    return (
      <span className="mute-btn" role="button" tabIndex="2" aria-label={this.state.muted ?  "Unmute chat sounds" : "Mute chat sounds"} onClick={(e) => this.onToggleMute(e)}  onKeyDown={(e) => { if (e.key == " " || e.code == "Space" || e.keyCode == 32) { this.onToggleMute(e) }} }>
        {this.state.muted ? <img className="muted" src={MutedIcon} alt="Muted" /> : <img src={UnmutedIcon} alt="Mute" />}
      </span>
    );
  }


  startCloseForm(e) {
    switch(this.props.status) {
      case 'chatting':
        // prevent minimize from triggering and open the closing window instead
        e.stopPropagation();
        this.setState({status: 'closing'});
        break;
      case 'thank_you':
        e.stopPropagation();
        this.closeForm(false);
    }
  }

  closeForm(sendData) {
    const { countryCode, headerText, onClose, sessionId: live_session } = this.props;
    this.setState({
      temporaryId: UUID.generate(),
      status: 'open',
      messages: new Map(),
      phone: countryCode,
      timeLeft: 30,
    });
    this.handleHeaderChange(headerText);
    this.lastPhone = countryCode;
    if (sendData) {
      ReliableRequest.dispatch({
        url: window.location.pathname,
        method: 'DELETE',
        data: { live_session },
        onSuccess: () => {},
      });
    }
    if (typeof onClose === 'function') onClose();
  }

  onSubmit(e) {
    e.preventDefault();

    window.getCaptcha('WebsiteWidet/submit', (recaptcha) => {
      this.routeForm(recaptcha);
    });
  }

  routeForm(recaptcha) {
    let data = Serialize(this.widgetForm.current);
    data = `${data}&recaptcha=${recaptcha}`;

    const { onStatusChange, status } = this.props;
    const { messages, temporaryId } = this.state;

    switch (status) {
      case 'qualification':
        const helpMessageTextarea = this.helpMessageTextarea;
        let helpMessage = helpMessageTextarea &&
                          helpMessageTextarea.value &&
                          helpMessageTextarea.value.trim();
        if (helpMessageTextarea) helpMessageTextarea.value = '';

        // only append the event if it's not empty
        if (helpMessage) {
          messages.set(temporaryId, {
            type: 'SmsMessage',
            id: temporaryId,
            direction: 'in',
            messageText: xss(helpMessage),
            status: 'queued',
            sentAt: (new Date()).getTime(),
          });
        }

        // always send chat request
        ReliableRequest.dispatch({
          url: `${window.location.pathname}/submit`,
          method: 'POST',
          data,
          onSuccess: (e) => {
            setTimeout(() => {
              onStatusChange(e.next_page);
              window.hideCaptcha(); // no longer using captcha in form once we submit
            }, 700);
          },
          onError: () => {
            this.closeForm(false);
          },
        });

        // try to predict the next state
        this.setState({
          closing: true,
        });
        setTimeout(() => {
          onStatusChange('chatting');
          // reset for animation
          this.setState({
            closing: false,
          });
        }, 700);
        break;
      case 'chatting':
        const chatMessageTextarea = this.chatMessageTextarea;
        let chatMessage = chatMessageTextarea &&
                          chatMessageTextarea.value &&
                          chatMessageTextarea.value.trim();
        if (chatMessageTextarea) chatMessageTextarea.value = '';

        // don't send any empty chat events (but still clean out the input)
        if (!chatMessage) break;

        messages.set(temporaryId, {
          type: 'SmsMessage',
          id: temporaryId,
          direction: 'in',
          messageText: xss(chatMessage),
          status: 'queued',
          sentAt: (new Date()).getTime(),
        });

        ReliableRequest.dispatch({
          url: `${window.location.pathname}/chat`,
          method: 'POST',
          data,
          onSuccess: () => {
            window.hideCaptcha(); // no longer using captcha
          },
          onError: () => {
            const { current } = this.chatInterface;
            // fetch the latest state of the ticket
            if (current && current.fetchMissedEvents) current.fetchMissedEvents();
          },
        });
        break;
    }
    this.setState({
      temporaryId: UUID.generate(),
      messages,
    });
    window.hideCaptcha();
  }

  handleHeaderChange(e) {
    this.setState({ title: e });
  }

  handleMarketingConsentCheck = ({ target: { checked } }) => this.setState({ marketingConsentChecked: checked });

  textWithUs(e) {
    e.preventDefault();

    window.getCaptcha('WebsiteWidet/textWithUs', (recaptcha) => {
      let data = Serialize(this.widgetForm.current);
      data = `${data}&recaptcha=${recaptcha}`;

      // send textWithUs request
      ReliableRequest.dispatch({
        url: `${window.location.pathname}/text_with_us`,
        maxAttempts: 1,
        method: 'POST',
        data,
        onSuccess: () => {
          window.hideCaptcha(); // no longer using captcha
        },
      });

      // just jump to the next state regardless
      this.props.onStatusChange('thank_you');
      this.handleHeaderChange("We'll be in touch.");
    });
  }

  setDropDownValue(value) {
    const {current} = this.websiteWidgetDropdown;

    if (current) current.setSelected({target: {value}});
  }
}

WebsiteWidgetForm.propTypes = { /* eslint-disable-line */
  className: PropTypes.string,
  widgetId: PropTypes.string,
  status: PropTypes.string,
  authenticityToken: PropTypes.string,
  sessionId: PropTypes.string,
  iframeDomain: PropTypes.string,
  initialDomain: PropTypes.string,
  disabled: PropTypes.bool,
  disabledHTML: PropTypes.string,
  countryCode: PropTypes.string,
  emailEnabled: PropTypes.bool,
  dropDownEnabled: PropTypes.bool,
  dropDownPrompt: PropTypes.string,
  dropDownOptions: PropTypes.array, /* eslint-disable-line */
  messageEnabled: PropTypes.bool,
  disclaimer: PropTypes.string,
  marketingConsentText: PropTypes.string,
  marketingConsentOptional: PropTypes.bool,
  headerText: PropTypes.string,
  liveChatText: PropTypes.string,
  textWithUsText: PropTypes.string,
  showNumber: PropTypes.bool,
  showNumberThankYou: PropTypes.bool,
  number: PropTypes.string,
  isMobile: PropTypes.bool,
  whiteLabel: PropTypes.bool,
  onMinimize: PropTypes.func,
  onClose: PropTypes.func,
  onStatusChange: PropTypes.func,
  shadowEnabled: PropTypes.bool,
};

export default WebsiteWidgetForm;
